import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Search, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CartSheet } from "@/components/CartSheet";
import { RoboxLeaderboard } from "@/components/RoboxLeaderboard";
import { useCart } from "@/context/CartContext";
import { api, fmtINR } from "@/lib/api";
import { CATEGORY_IMG, FEATURED_IMG, IMG } from "@/lib/content";
import { CATEGORY_HI } from "@/lib/content_hi";
import { useLang } from "@/context/LanguageContext";

const ProductCard = ({ p, kind, img, onAdd }) => {
  const { L } = useLang();
  return (
  <div className="product-tile bg-white text-[#0A192F] border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 rounded-xl overflow-hidden flex flex-col h-full" data-testid={`product-${p.id}`}>
    <div className="aspect-[4/3] bg-white overflow-hidden"><img src={img} alt={p.name} loading="lazy" className="product-img w-full h-full object-contain p-3" /></div>
    <div className="p-4 flex flex-col flex-1 border-t border-slate-200">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{p.sku || L(p.category, CATEGORY_HI[p.category])}</span>
      <h3 className="font-bold text-sm mt-1 leading-snug">{p.name}</h3>
      <p className={`text-xs text-slate-500 mt-1 flex-1 ${p.digital ? "" : "line-clamp-2"}`}>{p.description}</p>
      {p.digital && <p className="text-[11px] font-bold text-[#007A8A] bg-[#E6FAF8] rounded-lg p-2 mt-2" data-testid={`robox-note-${p.id}`}>Includes 1 month free full access. After 1 month, continue at ₹599/month or ₹4,999/year to keep trading features unlocked.</p>}
      <div className="flex items-center justify-between mt-3">
        <div>
          <div className="font-extrabold text-[#0A192F] font-['Outfit']" data-testid={`price-${p.id}`}>{fmtINR(p.selling_price_inr)}</div>
          {p.market_price_inr && <div className="text-[11px] text-slate-500">{L("Market", "बाज़ार")}: {fmtINR(p.market_price_inr)}</div>}
        </div>
        <Button size="sm" onClick={() => onAdd(p, kind)} className="h-10 px-4 sm:h-8 sm:px-3 rounded-full bg-[#FF3B3B] hover:bg-[#E63946] text-white active:scale-95" data-testid={`add-to-cart-${p.id}`}><Plus className="w-4 h-4" /> {L("Add", "जोड़ें")}</Button>
      </div>
    </div>
  </div>
  );
};

export default function Roboshop() {
  const { add } = useCart();
  const { L } = useLang();
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/catalog/categories").then((r) => setCats(r.data.categories));
    api.get("/catalog/featured").then((r) => setFeatured(r.data.items));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/catalog/components", { params: { category: cat, search: q || undefined } }).then((r) => setItems(r.data.items));
    }, 250);
    return () => clearTimeout(t);
  }, [cat, q]);

  const onAdd = (p, kind) => { add(p, kind); toast.success(L(`${p.name} added to cart`, `${p.name} कार्ट में जोड़ा गया`)); };

  return (
    <div className="bg-[#E8F0FE] min-h-screen" data-testid="roboshop-page">
      <section className="relative bg-[#F0F4F8] text-[#0A192F] overflow-hidden">
        <img src={IMG.circuit} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="rs-chip">{L("Roboshop", "रोबोशॉप")}</span>
            <h1 className="text-4xl sm:text-5xl font-extrabold mt-4">{L("Parts, kits & robots.", "पुर्ज़े, किट और रोबोट।")}</h1>
            <p className="text-slate-600 mt-2">{L("150+ components · Drone & robot frames · RoboSpark-branded robots · Robo-X trading app · Shipped across India", "150+ कंपोनेंट्स · ड्रोन और रोबोट फ़्रेम · RoboSpark ब्रांडेड रोबोट · Robo-X ट्रेडिंग ऐप · पूरे भारत में डिलीवरी")}</p>
          </div>
          <CartSheet />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <h2 className="text-2xl font-extrabold text-[#0A192F] mb-6">{L("RoboSpark Products", "RoboSpark उत्पाद")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p) => <ProductCard key={p.id} p={p} kind="product" img={FEATURED_IMG[p.id]} onAdd={onAdd} />)}
        </div>
      </section>
      <div className="-mt-4 mb-12"><RoboxLeaderboard /></div>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside className="lg:sticky lg:top-20 h-fit min-w-0">
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{L("Categories", "श्रेणियाँ")}</h3>
          <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2">
            {["All", ...cats].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-left whitespace-nowrap px-3 py-2 rounded-lg text-sm font-medium transition-colors ${cat === c ? "bg-[#FF3B3B] text-white" : "text-[#0A192F] hover:bg-slate-100"}`}>{L(c, CATEGORY_HI[c])}</button>
            ))}
          </div>
        </aside>
        <div className="min-w-0">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-6">
            <div className="relative w-full sm:max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Search components...", "कंपोनेंट खोजें...")} className="pl-9 h-11 bg-white" data-testid="shop-search" />
            </div>
            <span className="text-sm text-slate-500" data-testid="shop-count">{items.length} {L("components", "कंपोनेंट्स")}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
            {items.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.03 }}>
                <ProductCard p={p} kind="component" img={p.image || CATEGORY_IMG[p.category]} onAdd={onAdd} />
              </motion.div>
            ))}
          </div>
          {items.length === 0 && <p className="text-slate-500 py-10 text-center" data-testid="shop-empty">{L("No components match your search.", "आपकी खोज से कोई कंपोनेंट मेल नहीं खाता।")}</p>}
        </div>
      </section>
    </div>
  );
}
